import {
    useCallback,
    useEffect,
    useState,
    FormEventHandler,
    ReactElement,
    useRef
} from 'react';
import {
    UIButton,
    UIFormInput,
    UIFormInputRef,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader
} from '@/ui';
import { useAddTaskNoteAction } from '../actions';
import { useTasksTranslation } from '../hooks';

const HEIGHT = 32;

type AddNoteButtonProps = {
    taskId: string;
};

/**
 * The button that opens the modal below, and the only way into it.
 */
export function AddNoteButton({ taskId }: AddNoteButtonProps): ReactElement {
    const { t } = useTasksTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);

    function onNoteClick() {
        setIsModalOpen(true);
    }
    return (
        <>
            <UIButton
                variant="default"
                onClick={onNoteClick}
                customHeight={HEIGHT}
                leftIcon="edit-2"
            >
                {t('components.add_note_modal.button_label')}
            </UIButton>
            <AddNoteModal
                taskId={taskId}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </>
    );
}

type AddNoteModalProps = {
    taskId: string;
    isOpen: boolean;
    onClose: () => void;
};

export function AddNoteModal(props: AddNoteModalProps): ReactElement {
    const { taskId, isOpen, onClose } = props;

    const { addNote, loading } = useAddTaskNoteAction({ taskId });

    function handleClose() {
        onClose();
    }

    function onSubmit(data: { note: string }) {
        const { note } = data;
        addNote(note)
            .catch(() => {}) // Error handled in the action
            .finally(() => {
                handleClose();
            });
    }

    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            <AddNoteModalForm
                isOpen={isOpen}
                onClose={handleClose}
                loading={loading}
                onSubmit={onSubmit}
            />
        </UIModal>
    );
}

type AddNoteModalFormProps = {
    isOpen: boolean;
    loading: boolean;
    onClose: () => void;
    onSubmit: (data: { note: string }) => void;
};

function AddNoteModalForm(props: AddNoteModalFormProps): ReactElement {
    const { isOpen } = props;

    if (isOpen) {
        return <FormContent {...props} />;
    } else {
        return <></>;
    }
}

function FormContent(props: AddNoteModalFormProps): ReactElement {
    const { isOpen, loading, onClose, onSubmit } = props;
    const { t } = useTasksTranslation();
    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Note
     */
    const noteRef = useRef<UIFormInputRef>(null);
    const [note, setNote] = useState('');
    const [noteError, setNoteError] = useState('');
    const checkNote = useCallback(
        (note: string): string => {
            const noteTrimmed = note.trim();
            if (noteTrimmed.length === 0) {
                return t('shared.fields.note.error_required');
            } else if (noteTrimmed.length > 1000) {
                return t('shared.fields.note.error_too_long');
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setNoteError(checkNote(note));
        }
    }, [note, showRealTimeErrors, checkNote]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkNote(note)) {
            setShowRealTimeErrors(false);
            onSubmit?.({ note });
        }
    };

    useEffect(() => {
        if (isOpen) {
            noteRef.current?.focus();
        }
    }, [isOpen]);

    return (
        <>
            <form onSubmit={handleSubmit} noValidate>
                <UIModalHeader
                    title={t('components.add_note_modal.title')}
                    subtitle={t('components.add_note_modal.subtitle')}
                />
                <UIModalContent>
                    <UIFormInput
                        ref={noteRef}
                        name="note"
                        label={t('shared.fields.note.label')}
                        value={note}
                        type="text"
                        autoComplete="on"
                        errorText={noteError}
                        required
                        maxLength={1000}
                        onChange={setNote}
                        autoFocus
                    />
                </UIModalContent>
                <UIModalFooter
                    actions={[
                        {
                            variant: 'primary',
                            type: 'submit',
                            loading: loading,
                            disabled: loading,
                            text: t('shared.labels.submit')
                        },
                        {
                            text: t('shared.labels.cancel'),
                            disabled: loading,
                            onClick: onClose
                        }
                    ]}
                />
            </form>
        </>
    );
}
